/**
 * Post-authentication routing decision.
 *
 * ARCHITECTURAL RULE: Under Project Mint's universal-account model, a user
 * account may hold a Fan Passport AND workspace memberships (Artist Owner,
 * Artist Manager, Venue Manager, etc.) simultaneously. The global
 * `user.role` value is intentionally NOT authoritative for post-auth
 * routing — a Fan-passport-only account and an Artist Owner both carry
 * `role="fan"` today. This helper MUST NOT consult `user.role` to decide
 * where a freshly authenticated user should land.
 *
 * Routing priority (checked in order):
 *   1. Explicit `next` param from the sign-in/sign-up URL.
 *   2. Incomplete onboarding state for the caller-supplied intent
 *      (e.g. `intent === "artist"` and server says status != completed)
 *      → resume that onboarding.
 *   3. Existing workspace memberships (Artist first, then Venue) → workspace.
 *   4. Fallback: home ("/"). Fan Passport is a destination reached via the
 *      Passport button, not the automatic post-signup landing spot.
 *
 * The `intent` argument is the user's stated intent at signup (from the
 * intent picker, sessionStorage, or the durable OAuth-intent cookie). It
 * is a UI hint — the server-side onboarding_state record is the source of
 * truth once it exists.
 */
import { endpoints } from "@/lib/api";

const VALID_INTENTS = new Set(["artist", "venue", "promoter", "label", "fan"]);

/**
 * @param {object} args
 * @param {string=} args.explicitNext - `?next=` param from the URL (highest priority)
 * @param {string=} args.intent - Signup intent ("artist" | "venue" | "fan" | ...)
 * @returns {Promise<string>} pathname to navigate to
 */
export async function postAuthDestination({ explicitNext, intent } = {}) {
    if (explicitNext && typeof explicitNext === "string" && explicitNext.startsWith("/")) {
        return explicitNext;
    }

    const wantsArtist = intent === "artist";
    const wantsVenue = intent === "venue";

    // 2a. Explicit artist intent → check server-side onboarding state
    // and existing memberships to decide between resume vs. workspace.
    if (wantsArtist) {
        const [state, myArtists] = await Promise.all([
            safeCall(() => endpoints.onboardingState("artist")),
            safeCall(() => endpoints.authMyArtists()),
        ]);
        const artists = myArtists?.artists || [];
        const s = state?.state;
        const incomplete = s && s.status && s.status !== "completed";
        if (artists.length > 0 && !incomplete) return "/workspace";
        // Artist signup path — always land in the Artist setup form so the
        // new user can complete their profile. Server-side onboarding_state
        // is what makes cross-device resume possible; we do NOT rely on it
        // existing yet (SignUp / GoogleCallback upsert it before calling us).
        return "/onboarding/artist";
    }

    // 2b. Venue intent — mirror the Artist pattern.
    if (wantsVenue) {
        const myVenues = await safeCall(() => endpoints.authMyVenues());
        const venues = myVenues?.venues || [];
        if (venues.length > 0) return `/venue/${venues[0].id}/overview`;
        return "/onboarding/venue";
    }

    // 2c. Promoter / Label intents — new organisation kinds.
    if (intent === "promoter" || intent === "label") {
        const listFn = intent === "promoter" ? endpoints.authMyPromoters : endpoints.authMyLabels;
        const mine = await safeCall(listFn);
        const list = (mine && (mine.promoters || mine.labels)) || [];
        if (list.length > 0) return `/${intent}/${list[0].id}/overview`;
        return `/onboarding/${intent}`;
    }

    // 2c. No explicit intent from the current form (e.g. plain SignIn).
    // BEFORE defaulting to memberships/home, check for an incomplete
    // server-side onboarding_state — this is what powers cross-device
    // resume when a user signs back in on another browser.
    const artistState = await safeCall(() => endpoints.onboardingState("artist"));
    const artistStateRow = artistState?.state;
    const artistIncomplete = artistStateRow
        && artistStateRow.status
        && artistStateRow.status !== "completed";
    if (artistIncomplete) {
        // Only resume when the user has no artist memberships yet — a user
        // who completed onboarding on another device will have memberships
        // AND status="completed" already; the branch below routes them.
        const myArtistsForResume = await safeCall(() => endpoints.authMyArtists());
        if (!myArtistsForResume?.artists || myArtistsForResume.artists.length === 0) {
            return "/onboarding/artist";
        }
    }

    // 3. Existing workspace memberships from previous sessions.
    const [myArtists, myVenues] = await Promise.all([
        safeCall(() => endpoints.authMyArtists()),
        safeCall(() => endpoints.authMyVenues()),
    ]);
    const artists = myArtists?.artists || [];
    const venues = myVenues?.venues || [];
    if (artists.length > 0) return "/workspace";
    if (venues.length > 0) return `/venue/${venues[0].id}/overview`;

    // 4. Fallback: home. Fan Passport is a destination via the Passport
    // button in the nav — never the automatic post-signup landing.
    return "/";
}

async function safeCall(fn) {
    try { return await fn(); } catch { return null; }
}

export function normalizeIntent(raw) {
    if (!raw || typeof raw !== "string") return "";
    return VALID_INTENTS.has(raw) ? raw : "";
}
